import React, { useState } from 'react';
import { Box, Grid } from '@mui/material';
import { useDecision } from '@optimizely/react-sdk';

import IndividualDealStep from '@/builders/deals/steps/IndividualDealStep';

import { ShownOnDevice } from '@/common/ResponsiveContext';
import CenteredContainer from '@/common/CenteredContainer';
import CaloriesDisclaimer from '@/common/CaloriesDisclaimer/CaloriesDisclaimer';
import { EditBuilderSkeleton } from '@/common/EditBuilderSkeleton';
import AddToCartFooter from '@/common/AddToCartFooter';

import { SodiumWarningDisclaimer } from '@/clientCore/components/SodiumWarningDisclaimer/components/SodiumWarningDisclaimer';

import DealDetailsHeader from './components/DealDetailsHeader';
import AddDealToCartButton from './components/AddDealToCartButton';
import DealPrice from './components/DealPrice';

import { useDealDetails } from './hooks/useDealDetails';
import { useAddDealToCart } from './hooks/useAddDealToCart';

import { useStyles } from './DealDetails.styles';
import RecipeQuantitySelector from '@/coreComponents/buttons/RecipeQuantitySelector';
import formattedPrice from '@/common/formattedPrice/formattedPrice';

const BASE_QUANTITY = 1;
interface DealDetailsProps {
  quantity?: number;
  onChangeQuantity?: (q: number) => void;
}

const DealDetails = ({ quantity, onChangeQuantity }: DealDetailsProps = {}): JSX.Element => {
  const classes = useStyles();

  const {
    deal,
    onStepClick,
    summary,
    showEditBuilderSkeleton,
    multiStepSimplificationDecision,
    isShowSodiumAlertForStore
  } = useDealDetails();

  const [{ enabled: dealPriceForQuantitiyEnabled }] = useDecision('fr-ims-205-deal-price-for-quantity');
  const [{ enabled: isMultiStepQtyPickerEnabled }] = useDecision('fr-ims-200-multistep-select-enable');
  const [{ enabled: fixTotalUnitPriceEnabled }] = useDecision('fr-ims-682-fix-deal-total-unit-price');

  const maxCartQuantity = deal?.data?.maxCartQuantityAllowed ?? 10;
  const [localQty, setLocalQty] = useState<number>(BASE_QUANTITY);
  const isControlled = typeof quantity === 'number' && typeof onChangeQuantity === 'function';
  const effectiveQty = isControlled ? (quantity as number) : localQty;

  const totalUnitPrice = fixTotalUnitPriceEnabled
    ? parseFloat((summary?.totalPrice ?? '0').replace('$', '')) * 100
    : deal?.data?.price;

  const dealTotalPrice =
    (effectiveQty && totalUnitPrice)
      ? formattedPrice(effectiveQty * totalUnitPrice)
      : '0';

  const dealPriceToDisplay = dealPriceForQuantitiyEnabled
    ? dealTotalPrice
    : (summary?.totalPrice ?? '0');

  const {
    handleAddDealToCart,
    loading,
    ADD_DEAL_TO_CART_BUTTON_TEXT
  } = useAddDealToCart();


  const handleQuantityChange = (selectedQuantity: number) => {
    const next = isMultiStepQtyPickerEnabled
      ? Math.min(selectedQuantity, maxCartQuantity)
      : selectedQuantity;

    if (isControlled) {
      onChangeQuantity?.(next);
    } else {
      setLocalQty(next);
    }
  };


  if (!deal || !deal.data.id) {
    return <></>;
  }

  const showQuantity = deal.data.steps.length > 1;
  const quantityPickerDisabled =
    (deal?.data?.steps?.length ?? 0) !== (deal?.userSelections?.present?.recipes?.length ?? 0);

  return (
    <div data-testid="deal-builder">
      <CenteredContainer>
        <Grid className={classes.root} data-testid="build-your-deal-desktop">
          <DealDetailsHeader name={summary?.name ?? ''} price={summary?.price ?? '0'} />
          <Grid container wrap="nowrap">
            <Grid item className={classes.cardBox}>
              {showEditBuilderSkeleton ? (
                <EditBuilderSkeleton height="300px" />
              ) : (
                <Grid data-testid="deal-steps">
                  {deal.data.steps.map((step, index) => (
                    <IndividualDealStep
                      index={index}
                      key={`${step.id}-${index}`}
                      step={step}
                      onStepClick={onStepClick}
                      rules={deal.data.rules}
                      multiStepDecisionEnabled={multiStepSimplificationDecision.enabled}
                    />
                  ))}
                </Grid>
              )}
              <CaloriesDisclaimer />
              {isShowSodiumAlertForStore && <SodiumWarningDisclaimer />}
            </Grid>
            <ShownOnDevice mdUp>
              <Grid item className={classes.imageGrid}>
                <img
                  loading="lazy"
                  src={summary?.image}
                  alt=""
                  className={classes.img}
                />
              </Grid>
            </ShownOnDevice>
          </Grid>
        </Grid>
      </CenteredContainer>
      <AddToCartFooter>
        <CenteredContainer>
          <Box display="flex" alignItems="center">
            <Box marginRight={2}>
              <DealPrice price={dealPriceToDisplay} />
            </Box>
            <Grid className={classes.addToOrderContainer}>
              {showQuantity && (
                <RecipeQuantitySelector
                  quantity={effectiveQty}
                  onUpdateQuantity={handleQuantityChange}
                  disabled={quantityPickerDisabled}
                  maxQuantity={isMultiStepQtyPickerEnabled ? maxCartQuantity : undefined}
                />
              )}
              <Box className={classes.buttonContainer}>
                <AddDealToCartButton
                  text={ADD_DEAL_TO_CART_BUTTON_TEXT}
                  onClick={() => handleAddDealToCart(effectiveQty)}
                  loading={loading}
                  hasQuantitySelector={showQuantity}
                />
              </Box>
            </Grid>
          </Box>
        </CenteredContainer>
      </AddToCartFooter>
    </div>
  );
};

export default DealDetails;